
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  Card,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  getAllDailyPunchesAction,
  clearGetAllDailyPunchesAction,
  getDailyPunchByIdAction,
  clearGetDailyPunchByIdAction,
  getAllUserAction,
} from "../../../store/actions";

const handleView = (record, navigate) => {
  const id = record.travelId ?? record.id;
  if (id) {
    navigate(`/amrm/dailyActivityAMRM/punch/view/${id}`);
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    return `${date}  ${time}`;
  } catch {
    return iso;
  }
};

const getDistance = (record) => {
  const { totalDistanceKm, odometerStart, odometerEnd } = record;
  if (totalDistanceKm != null) return `${Number(totalDistanceKm)} km`;
  if (odometerStart != null && odometerEnd != null)
    return `${Math.max(0, Number(odometerEnd) - Number(odometerStart))} km`;
  return "-";
};

const getDateOnly = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toISOString().split("T")[0];
  } catch {
    return null;
  }
};

const isPreviousDayRecord = (record) => {
  if (!record?.startPunchTime) return false;
  const recordDate = getDateOnly(record.startPunchTime);
  const todayDate = getDateOnly(new Date().toISOString());
  return recordDate && todayDate && recordDate < todayDate;
};

const DESKTOP_COLS = "2fr 2fr 2fr 2fr 1.5fr 64px";
const HEADERS = ["Start Time", "Start Reading", "End Time", "End Reading", "Total Distance", "Actions"];

const TravelPunchingLogListAMRM = () => {
    // Month filter state: default to current month
    const [selectedMonth, setSelectedMonth] = useState(() => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    });
    // Track if Get Activity was clicked
    const [activityLoading, setActivityLoading] = useState(false);
    // Track if Get Activity was clicked for a non-logged-in user
    const [showOtherUserData, setShowOtherUserData] = useState(false);
      // No dialog/modal state needed for view

    // Fetch logs for selected user/month when Get Activity is clicked
    const handleGetActivity = () => {
      if (!selectedUserId || !selectedMonth) return;
      setActivityLoading(true);
      setShowOtherUserData(true);
      dispatch(clearGetAllDailyPunchesAction());
      // Calculate fromDate and toDate for selectedMonth
      const [year, month] = selectedMonth.split("-");
      const fromDate = `${year}-${month}-01`;
      const toDate = new Date(year, Number(month), 0); // last day of month
      const toDateStr = `${year}-${month}-${String(toDate.getDate()).padStart(2, "0")}`;
      dispatch(getAllDailyPunchesAction({ endPoint: `employee-daily-travel?user_id=${selectedUserId}&fromDate=${fromDate}&toDate=${toDateStr}` }));
      setTimeout(() => setActivityLoading(false), 1200); // Simulate loading
    };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState(() => {
    // Default to logged-in user
    return searchParams.get("userId") || (() => {
      try {
        return JSON.parse(localStorage.getItem("userDetails"))?.id ?? "";
      } catch {
        return "";
      }
    })();
  });

  const loggedInUserId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails"))?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const userRole = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails"))?.role ?? null;
    } catch {
      return null;
    }
  }, []);

  const isRestrictedRole = useMemo(() => {
    if (!userRole) return false;
    const normalizedRole = userRole.toLowerCase().trim();
    return ["admin", "ae", "zm"].includes(normalizedRole);
  }, [userRole]);

  const punchingLogPath = location.pathname.startsWith("/SOorMDO")
    ? "/SOorMDO/travelPunchingLog"
    : location.pathname.startsWith("/amrm")
    ? "/amrm/dailyActivity/punch"
    : "/masterData/travelPunchingLog";

  const { loading, successData, error, errorInfo } = useSelector(
    (state) => state.getAllDailyPunchesReducer || {}
  );

  const { successData: byIdSuccess } = useSelector((state) => state.getDailyPunchByIdReducer || {});

  const { successData: allUsersData } = useSelector(
    (state) => state.getAllUserReducer || {}
  );

  const records = useMemo(() => {
    if (!successData) return [];
    let allRecords = [];
    if (Array.isArray(successData?.data?.travels)) allRecords = successData.data.travels;
    else if (Array.isArray(successData?.data)) allRecords = successData.data;
    else if (Array.isArray(successData)) allRecords = successData;

    // If another user is selected and Get Activity was clicked, show only that user's activities (even if empty)
    if (showOtherUserData && selectedUserId && selectedUserId !== loggedInUserId) {
      return allRecords.filter((r) => r.userId === selectedUserId);
    }
    // If logged-in user is selected (or no user selected), always show their activities
    if (!selectedUserId || selectedUserId === loggedInUserId) {
      return allRecords.filter((r) => r.userId === loggedInUserId);
    }
    // Otherwise, don't show any data (until Get Activity is clicked)
    return [];
  }, [successData, showOtherUserData, selectedUserId, loggedInUserId]);

  const allUsers = useMemo(() => {
    if (!allUsersData) return [];

    let users = [];

    if (allUsersData?.data?.users && Array.isArray(allUsersData.data.users)) {
      users = allUsersData.data.users;
    } else if (allUsersData?.users && Array.isArray(allUsersData.users)) {
      users = allUsersData.users;
    } else if (Array.isArray(allUsersData?.data)) {
      users = allUsersData.data;
    } else if (Array.isArray(allUsersData)) {
      users = allUsersData;
    }

    const allowedRoles = ["RM", "AM", "SO", "MDO"];
    return users.filter((user) => {
      const role = user.role ? user.role.toUpperCase().trim() : "";
      return allowedRoles.includes(role);
    });
  }, [allUsersData]);

  useEffect(() => {
    dispatch(getAllUserAction());
    dispatch(clearGetAllDailyPunchesAction());
    // Always fetch logged-in user's activities for current month on mount
    if (selectedUserId === loggedInUserId || !selectedUserId) {
      dispatch(getAllDailyPunchesAction({ endPoint: `employee-daily-travel?userId=${loggedInUserId}&month=${selectedMonth}` }));
      setShowOtherUserData(false);
    }
    // eslint-disable-next-line
  }, [dispatch]);

  // When selectedUserId or selectedMonth changes
  useEffect(() => {
    // Always clear activities when user/month changes
    dispatch(clearGetAllDailyPunchesAction());
    // If logged-in user is selected, always show their activities for selected month
    if (selectedUserId === loggedInUserId || !selectedUserId) {
      dispatch(getAllDailyPunchesAction({ endPoint: `employee-daily-travel?userId=${loggedInUserId}&month=${selectedMonth}` }));
      setShowOtherUserData(false);
    } else {
      // For other users, hide data until Get Activity is clicked
      setShowOtherUserData(false);
    }
    // eslint-disable-next-line
  }, [selectedUserId, selectedMonth, loggedInUserId, dispatch]);

  useEffect(() => {
    if (byIdSuccess) {
      const record = byIdSuccess?.data ?? byIdSuccess;
      dispatch(clearGetDailyPunchByIdAction());
      navigate(punchingLogPath, { state: { editRecord: record } });
    }
  }, [byIdSuccess, dispatch, navigate, punchingLogPath]);

  const incompleteData = useMemo(() => {
    if (isRestrictedRole && selectedUserId && selectedUserId !== loggedInUserId) {
      return null;
    }

    const isNonWorking = (record) => {
      const status = String(record?.dayStatus ?? record?.day_status ?? "").toUpperCase();
      return status === "LEAVE" || status === "HOLIDAY";
    };

    // Find the most recent incomplete record for the logged-in user (not LEAVE/HOLIDAY)
    const incompleteRec = records.find(
      (r) =>
        r.userId === loggedInUserId &&
        (r.endPunchTime === null || r.endPunchTime === undefined) &&
        !isNonWorking(r)
    );

    if (!incompleteRec) return null;

    return {
      record: incompleteRec,
      isPreviousDay: isPreviousDayRecord(incompleteRec),
    };
  }, [records, loggedInUserId, isRestrictedRole, selectedUserId]);

  const handleEdit = (record) => {
    const id = record.travelId ?? record.id;
    dispatch(getDailyPunchByIdAction({ endPoint: `employee-daily-travel/${id}` }));
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);
    setShowOtherUserData(false);
    if (userId) {
      setSearchParams({ userId });
    } else {
      setSearchParams({});
    }
  };


  // Export handler for Excel
  const handleExportExcel = () => {
    if (!records || records.length === 0) return;
    const exportData = records.map((record) => ({
      "Travel Date": record.travelDate ?? '',
      "Start Time": formatDateTime(record.startPunchTime),
      "Start Reading": record.odometerStart ?? '-',
      "End Time": formatDateTime(record.endPunchTime),
       "End Reading": record.odometerEnd ?? '-',
      "Total Distance (km)": getDistance(record).replace(" km", ""),
      
     
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Travel Logs");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const filename = `TravelLogs_${selectedUserId || "All"}_${selectedMonth}.xlsx`;
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), filename);
  };

  return (
    <Box sx={{ p: 2, minHeight: "100vh",mt: { xs: 0, sm: -5, md: 0 } }}>
      <Typography variant="h6" fontWeight={700} sx={{ mt: { xs: 0, sm: 5, md: 0 }, mb: 2 }}>
        Travel Punching Logs
      </Typography>

 <Card sx={{ mb: 3 }}>
  <Grid container spacing={2} alignItems="center" p={2}>

    {/* Select User */}
    <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
      <FormControl fullWidth size="small">
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
          Select User
        </Typography>
        <Select
          value={selectedUserId}
          onChange={handleUserChange}
          displayEmpty
        >
          <MenuItem value="">All Users</MenuItem>
          {allUsers.length === 0 ? (
            <MenuItem disabled>No users found (RM/AM/SO/MDO)</MenuItem>
          ) : (
            allUsers.map((user) => (
              <MenuItem key={user.userId} value={user.userId}>
                {`${user.name || user.userName || `User ${user.userId}`} (${user.role ? user.role.toUpperCase() : ""})`}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Grid>

    {/* Select Month */}
    <Grid item size={{ xs: 6, sm: 6, md: 3 }}>
      <FormControl fullWidth size="small">
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5 }}>
          Select Month
        </Typography>
        <Select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          displayEmpty
        >
          {Array.from({ length: 12 }).map((_, idx) => {
            const now = new Date();
            const m = new Date(now.getFullYear(), now.getMonth() - idx, 1);
            const value = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
            const label = m.toLocaleString("en-IN", { month: "long", year: "numeric" });

            return (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Grid>

    {/* Get Activity */}
    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
      <Button
        fullWidth
        variant="contained"
        sx={{ borderRadius: 2, fontWeight: 500, mt: { xs: 2, sm: 3 } }}
        disabled={!selectedUserId || !selectedMonth || activityLoading}
        onClick={handleGetActivity}
      >
        {activityLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          "Get Activity"
        )}
      </Button>
    </Grid>

    {/* Export */}
    <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
      <Button
        fullWidth
        variant="outlined"
        color="secondary"
        sx={{ borderRadius: 2, fontWeight: 600, mt: { xs: 2, sm: 3 } }}
        disabled={records.length === 0}
        onClick={handleExportExcel}
      >
        Export
      </Button>
    </Grid>

    {/* Add Punching */}
    <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
      <Button
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => navigate(punchingLogPath)}
        disabled={!!(incompleteData && incompleteData.isPreviousDay)}
        sx={{ borderRadius: 2, mt: { xs: 2, sm: 3 } }}
      >
        Add Punching
      </Button>
    </Grid>

  </Grid>
</Card>
      {!loading && incompleteData && !isRestrictedRole && incompleteData.isPreviousDay && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button
              color="error"
              size="small"
              variant="outlined"
              onClick={() => handleEdit(incompleteData.record)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Complete Now
            </Button>
          }
        >
          <AlertTitle>Pending Day Closure</AlertTitle>
          Complete end punch to start the new day punching.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorInfo || "Failed to load travel punching logs."}
        </Alert>
      )}

      {!loading && !error && records.length === 0 && (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <DirectionsCarIcon sx={{ fontSize: 56, color: "#C7D2FE", mb: 1 }} />
          <Typography color="text.secondary" fontWeight={600}>
            No travel logs found
          </Typography>
        </Box>
      )}

      {!loading && records.length > 0 && (
        
        <Box>
          <Grid container spacing={2} sx={{ display: { xs: "flex", sm: "none" } }}>
            {records.map((record, idx) => {
              const isAdmin = userRole?.toLowerCase()?.trim() === "admin";
              let userName = "";
              if (isAdmin) {
                const userObj = allUsers.find(u => u.userId === record.userId);
                userName = userObj ? (userObj.name || userObj.userName || `User ${userObj.userId}`) : record.userId;
              }
              return (
                <Grid item size={{ xs: 12 }} key={record.travelId ?? idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      p: 1.5,
                      border: "1px solid #E5E7EB",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover, &:active": {
                        borderColor: "#2563EB",
                        boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                      },
                    }}
                  >
                    {(['LEAVE', 'HOLIDAY'].includes(String(record.dayStatus ?? record.day_status ?? '').toUpperCase())) ? (
                      <>
                        <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>Date:</Typography>
                          <Typography variant="body2">{record.travelDate || '-'}</Typography>
                        </Box>
                        <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>Status:</Typography>
                          <Typography variant="body2" color="error.main" fontWeight={600}>
                            {String(record.dayStatus ?? record.day_status ?? '').toUpperCase()}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>Start Time:</Typography>
                          <Typography variant="body2">{formatDateTime(record.startPunchTime)}</Typography>
                        </Box>
                         <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>Start Reading:</Typography>
                          <Typography variant="body2">{record.odometerStart ?? '-'}</Typography>
                        </Box>
                        <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>End Time:</Typography>
                          <Typography variant="body2">{formatDateTime(record.endPunchTime)}</Typography>
                        </Box>
                         <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>End Reading:</Typography>
                          <Typography variant="body2">{record.odometerEnd ?? '-'}</Typography>
                        </Box>
                        <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                          <Typography fontWeight={600} minWidth={120}>Total Distance:</Typography>
                          <Typography variant="body2">{getDistance(record)}</Typography>
                        </Box>
                       
                       
                        {isAdmin && (
                          <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                            <Typography fontWeight={600} minWidth={120}>User:</Typography>
                            <Typography variant="body2">{userName}</Typography>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={1}>
                          {(!record.endPunchTime) ? (
                            <IconButton size="small" onClick={() => handleEdit(record)}>
                              <EditOutlinedIcon
                                fontSize="small"
                                sx={{ color: "#2563EB", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.25))" }}
                              />
                            </IconButton>
                          ) : (
                            <IconButton size="small" aria-label="view" onClick={() => {
                              const id = record.travelId ?? record.id;
                              if (id) {
                                navigate(`/amrm/dailyActivityAMRM/punch/view/${id}`);
                              }
                            }}>
                              <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                            </IconButton>
                          )}
                        </Box>
                      </>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", gap: 1 }}>
            {/* Header row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: DESKTOP_COLS,
                px: 1.5,
                py: 1,
                gap: 1,
              }}
            >
              {HEADERS.map((h) => (
                <Typography
                  key={h}
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    textTransform: "uppercase",
                    textAlign: h === "Actions" ? "right" : "left",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: h === "Actions" ? "flex-end" : "flex-start",
                    minHeight: 32
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {/* Value rows */}
            {records.map((record, idx) => {
              const isComplete = !!record.startPunchTime && !!record.endPunchTime;
              const isAdmin = userRole?.toLowerCase()?.trim() === "admin";
              const dayStatus = String(record.dayStatus ?? record.day_status ?? '').toUpperCase();
              let userName = "";
              let userRoleLabel = "";
              if (isAdmin) {
                const userObj = allUsers.find(u => u.userId === record.userId);
                userName = userObj && userObj.name ? userObj.name : "Unknown User";
                userRoleLabel = userObj && userObj.role ? userObj.role : "Unknown Role";
              }
              // If HOLIDAY or LEAVE, show only status and date, no actions
              if (dayStatus === "HOLIDAY" || dayStatus === "LEAVE") {
                return (
                  <Paper
                    key={record.travelId ?? idx}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      display: "grid",
                      gridTemplateColumns: DESKTOP_COLS,
                      alignItems: "center",
                      border: "1px solid #E5E7EB",
                      px: 2,
                      py: 1.5,
                      gap: 1,
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        borderColor: "#2563EB",
                        boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                      },
                    }}
                  >
                    <Typography variant="body2">{record.travelDate || '-'}</Typography>
                    <Typography variant="body2" color="error.main" fontWeight={600}>{dayStatus}</Typography>
                    <Typography variant="body2">-</Typography>
                    <Typography variant="body2">-</Typography>
                    <Typography variant="body2">-</Typography>
                    <Box />
                  </Paper>
                );
              }
              // Otherwise, show normal fields and actions
              return (
                <Paper
                  key={record.travelId ?? idx}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    display: "grid",
                    gridTemplateColumns: DESKTOP_COLS,
                    alignItems: "center",
                    border: "1px solid #E5E7EB",
                    px: 2,
                    py: 1.5,
                    gap: 1,
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: "#2563EB",
                      boxShadow: "0 4px 14px rgba(37,99,235,0.18)",
                    },
                  }}
                >
                  <Typography variant="body2">{formatDateTime(record.startPunchTime)}</Typography>
                  <Typography variant="body2">{record.odometerStart ?? '-'}</Typography>
                  <Typography variant="body2">{formatDateTime(record.endPunchTime)}</Typography>
                  <Typography variant="body2">{record.odometerEnd ?? '-'}</Typography>
                  <Typography variant="body2">{getDistance(record)}</Typography>
                  {/* {isAdmin && <Typography variant="body2">{userName} ({userRoleLabel})</Typography>} */}
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{ height: '100%' }}
                  >
                      {isAdmin ? (
                        <IconButton size="small" aria-label="view" onClick={() => handleView(record, navigate)}>
                          <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                        </IconButton>
                      ) : (
                        isComplete ? (
                          <IconButton size="small" aria-label="view" onClick={() => handleView(record, navigate)}>
                            <VisibilityIcon fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleEdit(record)}>
                            <EditOutlinedIcon fontSize="small" sx={{ color: "#2563EB", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.25))" }} />
                          </IconButton>
                        )
                      )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

        {/* No dialog/modal for TravelPunchingLogViewAMRM, only route navigation is used */}

    </Box>
  );
};

export default TravelPunchingLogListAMRM;
